import { useEffect, useRef, useState } from "react";
import { FileUp, PenLine, Loader2, Sparkles, CheckCircle2, Circle, CircleAlert, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";

const STAGES = ["Lecture du document", "Identification des parties", "Extraction des conditions", "Préparation des informations"].map((s) => tfr(s));

export function ContractImportDialog({ open, onOpenChange, onManual, onExtracted, startStep = "choose", preservedFile = null }) {
  const { tf } = useLang();
  const [step, setStep] = useState("choose");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [stage, setStage] = useState(0);
  const aliveRef = useRef(false);

  useEffect(() => {
    if (open) { setStep(startStep); setFile(preservedFile); setError(""); setStage(0); aliveRef.current = true; }
    else { aliveRef.current = false; }
  }, [open, startStep, preservedFile]);

  useEffect(() => {
    if (step !== "analyzing") return;
    setStage(0);
    const iv = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 1700);
    return () => clearInterval(iv);
  }, [step]);

  const analyze = async (f) => {
    if (!f) return;
    setFile(f); setError(""); setStep("analyzing");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/contracts/import-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!aliveRef.current) return;
      toast.success(`${tf("Analyse terminée")} — ${r.data.detected} ${tf("informations")}`);
      onExtracted?.(r.data, f);
    } catch (e) {
      if (!aliveRef.current) return;
      setError(e?.response?.data?.detail || "");
      setStep("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="contract-chooser-dialog">
        {step === "choose" && (
          <>
            <DialogTitle>{tf("Comment souhaitez-vous créer ce contrat ?")}</DialogTitle>
            <DialogDescription className="sr-only">{tf("Comment souhaitez-vous créer ce contrat ?")}</DialogDescription>
            <div className="space-y-3">
              <button type="button" onClick={() => setStep("import")} data-testid="chooser-import-btn"
                className="w-full text-left rounded-2xl border border-red-500/30 bg-red-500/5 p-4 hover:bg-red-500/10 transition-colors motion-reduce:transition-none">
                <div className="flex items-center gap-3">
                  <FileUp className="h-5 w-5 text-red-500 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">{tf("Importer un contrat")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{tf("Ajoutez un document. Athlynx extrait les informations et prépare le dossier.")}</p>
                  </div>
                </div>
              </button>
              <button type="button" onClick={onManual} data-testid="chooser-manual-btn"
                className="w-full text-left rounded-2xl border border-border dark:border-border p-4 hover:bg-foreground/5 transition-colors motion-reduce:transition-none">
                <div className="flex items-center gap-3">
                  <PenLine className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">{tf("Créer manuellement")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{tf("Renseignez vous-même les informations du contrat.")}</p>
                  </div>
                </div>
              </button>
            </div>
          </>
        )}

        {step === "import" && (
          <>
            <DialogTitle>{tf("Importer un contrat")}</DialogTitle>
            <DialogDescription>{tf("Ajoutez un document. Athlynx extrait les informations et prépare le dossier.")}</DialogDescription>
            {file && (
              <div className="flex items-center gap-2.5 rounded-xl border border-border dark:border-border px-3 py-2.5" data-testid="import-preserved-file">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm flex-1 truncate">{file.name}</span>
                <Button size="sm" className="h-8" onClick={() => analyze(file)} data-testid="import-reanalyze-btn">{tf("Analyser à nouveau")}</Button>
              </div>
            )}
            <label className="rounded-2xl border border-dashed border-border dark:border-border p-8 flex flex-col items-center gap-2 cursor-pointer hover:bg-foreground/5 transition-colors motion-reduce:transition-none">
              <FileUp className="h-6 w-6 text-muted-foreground" />
              <span className="text-sm font-medium">{file ? tf("Choisir un autre document") : tf("Choisir un document (PDF)")}</span>
              <input type="file" accept=".pdf,.txt" className="hidden"
                onChange={(e) => analyze(e.target.files?.[0])} data-testid="import-file-input" />
            </label>
          </>
        )}

        {step === "analyzing" && (
          <>
            <DialogTitle className="sr-only">Athlynx Intelligence</DialogTitle>
            <DialogDescription className="sr-only">{tf("Analyse du contrat en cours…")}</DialogDescription>
            <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.03] p-5 space-y-4" role="status" aria-live="polite" data-testid="import-analyzing">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/25 flex items-center justify-center shrink-0">
                  <Sparkles className="h-5 w-5 text-red-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Athlynx Intelligence</p>
                  <p className="text-xs text-muted-foreground">{tf("Analyse du contrat en cours…")}</p>
                </div>
              </div>
              <ul className="space-y-2.5">
                {STAGES.map((s, i) => (
                  <li key={s} className={`flex items-center gap-2.5 text-sm ${i <= stage ? "text-foreground" : "text-muted-foreground/60"}`}>
                    {i < stage ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      : i === stage ? <Loader2 className="h-4 w-4 text-red-500 animate-spin motion-reduce:animate-none shrink-0" />
                        : <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />}
                    {tf(s)}
                  </li>
                ))}
              </ul>
              {file && <p className="text-[11px] text-muted-foreground truncate">{file.name}</p>}
            </div>
          </>
        )}

        {step === "error" && (
          <>
            <DialogTitle className="flex items-center gap-2"><CircleAlert className="h-4 w-4 text-red-500" /> {tf("L'analyse du document n'a pas pu être terminée.")}</DialogTitle>
            <DialogDescription data-testid="import-error-msg">
              {error || tf("Vous pouvez réessayer ou compléter les informations manuellement.")}
              {error ? ` ${tf("Vous pouvez réessayer ou compléter les informations manuellement.")}` : ""}
            </DialogDescription>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setStep("import")} data-testid="import-error-change-file">{tf("Choisir un autre document")}</Button>
              <Button variant="outline" size="sm" onClick={onManual} data-testid="import-error-manual-btn">{tf("Continuer manuellement")}</Button>
              <Button size="sm" onClick={() => analyze(file)} disabled={!file} data-testid="import-error-retry-btn">{tf("Réessayer")}</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
